import { put, call, take, select } from 'redux-saga/effects';
import get from 'lodash/get';
import * as types from '../../constants/ActionTypes';
import findRoutePoint from '../../functions/route/findRoutePoint';
import {
  editRoutePoint,
  rearrangeRoutePoints,
  removeRoutePoint,
} from '../../actions/route';
import { editEvent } from '../../actions/quests';
import { passEventParams } from '../../actions/events';
import { findQuest } from '../../reducers/questsHelpers';
import { uploadBatch } from '../uploads';
import { syncQuest, isSyncQuestEnding } from './questsSyncSaga';

/**
 * Reorders events and removes route points bindings to moved event
 * @param {Object} action
 */
export function* reorderEventsAndPoints(action) {
  if (yield select((state) => state.quests.isSyncing)) {
    yield take(isSyncQuestEnding);
  }

  const { ids: { questId, eventId } = {} } = action;
  const quest = findQuest(
    yield select((state) => state.quests.quests),
    questId
  );
  const points = quest.routes || [];
  const eventIds = quest.events.map(({ id }) => id);

  yield put({
    ...action,
    type: types.REORDER_EVENTS,
  });
  yield put({
    type: types.SYNC_QUEST,
    id: questId,
    onSyncStart: action.onSyncStart,
  });

  const dispatchedAction = yield take(isSyncQuestEnding);
  if (dispatchedAction.type !== types.SYNC_QUEST_SUCCESS) return;

  const pointIndex = points.findIndex((p) => p.guid === eventId);
  if (pointIndex > -1) {
    if (get(points[pointIndex], 'events.length')) {
      yield put(
        editRoutePoint({
          questId,
          guid: eventId,
          order: points[pointIndex].order,
          events: [],
        })
      );
    }

    let nextPointIndex = points.length;
    for (let i = action.toIndex; i < eventIds.length; i += 1) {
      const pi = points.findIndex((p) => p.guid === eventIds[i]);
      if (pi > -1) {
        nextPointIndex = pi;
        break;
      }
    }

    if (nextPointIndex !== pointIndex) {
      yield put(
        rearrangeRoutePoints({
          questId,
          pointId: points[pointIndex].guid,
          newPointId: get(points[nextPointIndex], 'guid'),
        })
      );
    }
  }

  const point = points.find((p) => p.events && p.events.includes(eventId));
  if (point) {
    const boundEvents = [...point.events];
    boundEvents.splice(eventId, 1);
    yield put(
      editRoutePoint({
        questId,
        guid: point.guid,
        order: point.order,
        events: boundEvents,
      })
    );
  }
}

/**
 * Saga updates image in event.content[] images array
 */
export function* replaceImage(action) {
  yield put({
    type: types.REMOVE_EVENT_IMAGE,
    ...action.eventPK,
    imageId: action.imageId,
  });
  yield call(uploadBatch, action);
}

/**
 * Saga changes image position in event.content[] images array
 */
export function* dragImage(action) {
  yield put({
    type: types.DRAG_EVENT_IMAGE,
    questId: action.questId,
    eventId: action.eventId,
    newImageId: action.newImageId,
    imageId: action.imageId,
    mediaId: action.mediaId,
    newMediaId: action.newMediaId,
    isUpload: action.isUpload || false,
  });
  if (!action.isUpload) {
    yield call(syncQuest, { id: action.questId, ...action });
  }
}

/**
 * Toggles museum flag and syncs changes
 */
export function* toggleMuseum(action) {
  const { questId, type: _type, ...rest } = action;
  yield put({ type: types.TOGGLE_MUSEUM, questId, ...rest });
  yield call(syncQuest, { id: questId, updatePlans: true });
}

/**
 * Deletes event and cleans up bound route points
 */
export function* deleteEvent({ questId, eventId }) {
  const quests = yield select((state) => state.quests.quests);
  const quest = findQuest(quests, questId);
  const point = findRoutePoint(get(quest, 'routes', []), eventId);

  yield put(
    editEvent({
      type: types.DELETE_EVENT,
      ...passEventParams({ questId, eventId }),
    })
  );

  if (point) {
    yield put(
      removeRoutePoint({
        questId,
        pointId: eventId,
        isSync: false,
        isSend: false,
      })
    );

    const endOfSyncQuest = yield take(isSyncQuestEnding);
    if (endOfSyncQuest.type === types.SYNC_QUEST_SUCCESS) {
      yield put(
        removeRoutePoint({
          questId,
          pointId: eventId,
          isSync: false,
        })
      );
    }
  }
}
